import StandardButton from '@atlaskit/button/standard-button';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import { ButtonClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { Button, H4, PartialOutageError } from '@atlassian/mpac-primitives';
import { AppDeployment, HostingDisplayName, HostingTypeToQueryParam } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { ErrorBoundary } from '@atlassian/mpac-ui';
import { log } from '@atlassian/mpac-utils';
import React, { useEffect, useState } from 'react';
import { ANALYTICS_REVIEW_WRITE_REVIEW_BUTTON } from '../../../../../analytics/Events';
import { ELEMENT_ID, TEST_ID } from '../../../../../constants';
import { useReviews } from '../../../../../hooks/useReviews';
import { EmptyReviews } from '../EmptyReviews';
import { ReviewFormModal } from '../ReviewFormModal';
import UserReviewsV2 from '../UserReviewsV2';
import { OwnReview } from '../UserReviewsV2/OwnReview';
import { ReviewsHeader } from '../UserReviewsV2/ReviewsHeader';
import { Container, EmptyReviewsWrapper, ErrorContentWrapper, ErrorWrapper, HeadingWrapper, LeftSection, MoreReviewsButtonWrapper, ResponsiveContainer, RightSection, UserReviewsWrapper } from './ReviewsContainerV2.styles';
import { ReviewsLeftColumn } from './ReviewsLeftColumn';
import { ReviewsLoadMoreSkeleton, ReviewsSkeleton } from './ReviewsSkeleton';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// eslint-disable-next-line max-lines-per-function,complexity
const ReviewsContainerV2 = _ref => {
  var _cloudCompatibleProdu, _cloudCompatibleProdu2, _reviewsData$selfUser, _reviewsData$selfUser2;
  let {
    hosting,
    mpacBaseUrl,
    appKey,
    user,
    loginHref,
    vendorName,
    vendorLogo,
    onSeeMoreReviewsClick,
    appRating,
    cloudCompatibleProducts,
    appDeploymentType,
    productHostingOptions,
    isServerAppEos
  } = _ref;
  // each app is compatible with only single product
  const compatibleProduct = (_cloudCompatibleProdu = cloudCompatibleProducts === null || cloudCompatibleProducts === void 0 ? void 0 : (_cloudCompatibleProdu2 = cloudCompatibleProducts[0]) === null || _cloudCompatibleProdu2 === void 0 ? void 0 : _cloudCompatibleProdu2.id) !== null && _cloudCompatibleProdu !== void 0 ? _cloudCompatibleProdu : '';
  const isAppCompatibleWithJiraOrConfluence = ['jira', 'confluence'].includes(compatibleProduct);
  const isConnectOrCloudApp = appDeploymentType === AppDeployment.CONNECT || appDeploymentType === AppDeployment.ECOSYSTEM_CLOUD;
  const isReviewApplicableForComplianceLogic = hosting === AtlassianProductHostingType.Cloud && isConnectOrCloudApp && isAppCompatibleWithJiraOrConfluence;
  const {
    reviewsData,
    showReviewsLoader,
    showSortLoader,
    sortReviewsBy,
    error,
    showMoreReviewsLoader,
    getMoreReviews,
    filterOutDeletedReview,
    currentSortOption,
    renderPostedReview,
    refetchReviews,
    filterOutDeletedVendorResponse
  } = useReviews({
    hosting: HostingTypeToQueryParam[hosting],
    mpacBaseUrl,
    appKey,
    isReviewApplicableForComplianceLogic,
    isAetherEnabled: true
  });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const ownReviewId = reviewsData === null || reviewsData === void 0 ? void 0 : (_reviewsData$selfUser = reviewsData.selfUserReview) === null || _reviewsData$selfUser === void 0 ? void 0 : _reviewsData$selfUser.id;
  const ownReviewStatus = reviewsData === null || reviewsData === void 0 ? void 0 : (_reviewsData$selfUser2 = reviewsData.selfUserReview) === null || _reviewsData$selfUser2 === void 0 ? void 0 : _reviewsData$selfUser2.status;
  const bannerType = ownReviewStatus === 'pending' ? 'warning' : null;
  const [showReviewBanner, setShowReviewBanner] = useState(bannerType);
  const [showDeleteFlag, setShowDeleteFlag] = useState(false);
  let noOwnReviewPosted;
  if (reviewsData !== null && reviewsData !== void 0 && reviewsData.selfUserReview) {
    // if user review status is approved, then we don't get its status in the response
    noOwnReviewPosted = ownReviewStatus !== undefined;
  } else {
    // if self user review is not present in the response, then it means user has not posted any review
    noOwnReviewPosted = true;
  }
  const [ownReviewUpdated, setOwnReviewUpdated] = useState(false);
  useEffect(() => {
    if (bannerType === 'warning' && showReviewBanner !== 'feedback') {
      setShowReviewBanner(bannerType);
    }
  }, [bannerType]);
  const fireAnalyticsEvent = useAnalyticsEvents();
  const onShowReviewForm = () => {
    setShowReviewForm(true);
    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      actionSubjectId: ANALYTICS_REVIEW_WRITE_REVIEW_BUTTON,
      attributes: {
        label: 'Write a Review'
      }
    });
  };
  const postReviewHandler = (postedReview, isEdit) => {
    setShowReviewForm(false);
    if (isReviewApplicableForComplianceLogic && ownReviewStatus !== 'approved') {
      setShowReviewBanner('feedback');
    } else if (isEdit) {
      setOwnReviewUpdated(true);
    }
    renderPostedReview(postedReview);
  };
  if (showReviewsLoader) {
    return /*#__PURE__*/_jsx(ReviewsSkeleton, {});
  }
  const handleSeeMoreReviews = () => {
    onSeeMoreReviewsClick();
    getMoreReviews();
  };
  const handleFilterOutDeletedReview = deletedReviewEndpoint => {
    filterOutDeletedReview(deletedReviewEndpoint);
    setShowDeleteFlag(true);
  };
  const fallbackUI = () => {
    return /*#__PURE__*/_jsx(ErrorWrapper, {
      children: /*#__PURE__*/_jsx(PartialOutageError, {
        errorMessage: " ",
        children: /*#__PURE__*/_jsxs(ErrorContentWrapper, {
          children: [/*#__PURE__*/_jsx(StandardButton, {
            spacing: "none",
            appearance: "link",
            onClick: refetchReviews,
            children: "Reload the section"
          }), "or try again in some time"]
        })
      })
    });
  };
  if (reviewsData) {
    const postReviewUrl = "".concat(mpacBaseUrl).concat(reviewsData.selfEndpoint);
    return /*#__PURE__*/_jsxs(ErrorBoundary, {
      boundaryName: "marketplaceReviewsTab",
      fallback: fallbackUI(),
      children: [/*#__PURE__*/_jsx(FlagGroup, {
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [showReviewBanner === 'feedback' && /*#__PURE__*/_jsx(AutoDismissFlag, {
            id: "review-submitted",
            icon: /*#__PURE__*/_jsx(EditorSuccessIcon, {
              label: "Success",
              primaryColor: "var(--ds-icon-success, #22A06B)"
            }),
            title: "Review submitted",
            description: "Thank you for your review and it\u2019s under consideration. We\u2019ll notify you via e-mail once the process is complete.",
            onDismissed: () => setShowReviewBanner(null),
            actions: [{
              content: 'Got it',
              onClick: () => setShowReviewBanner(null)
            }]
          }, 1), showDeleteFlag && /*#__PURE__*/_jsx(AutoDismissFlag, {
            id: "review-deleted",
            icon: /*#__PURE__*/_jsx(EditorSuccessIcon, {
              label: "Success",
              primaryColor: "var(--ds-icon-success, #22A06B)"
            }),
            title: "Review deleted",
            description: "We\u2019ve removed your review and it will no longer be available on the website.",
            onDismissed: () => setShowDeleteFlag(false),
            actions: [{
              content: 'Got it',
              onClick: () => setShowDeleteFlag(false)
            }]
          }, 2)]
        })
      }), showReviewForm && /*#__PURE__*/_jsx(ReviewFormModal, {
        postReviewUrl: postReviewUrl,
        hosting: hosting,
        onReviewPost: postReviewHandler,
        onClose: () => setShowReviewForm(false),
        selfUserReview: reviewsData.selfUserReview,
        isReviewApplicableForComplianceLogic: isReviewApplicableForComplianceLogic
      }), /*#__PURE__*/_jsx(Container, {
        children: reviewsData.reviews.length == 0 && noOwnReviewPosted && !showSortLoader ? /*#__PURE__*/_jsxs(EmptyReviewsWrapper, {
          children: [/*#__PURE__*/_jsx(HeadingWrapper, {
            children: /*#__PURE__*/_jsxs(H4, {
              as: "h2",
              "data-testid": TEST_ID.REVIEWS_TAB_HEADING,
              children: ["Reviews for", ' ', hosting === AtlassianProductHostingType.DataCenter ? HostingDisplayName[hosting] : HostingDisplayName[hosting].toLowerCase()]
            })
          }), /*#__PURE__*/_jsx(EmptyReviews, {
            hosting: hosting,
            onClick: onShowReviewForm,
            loginRef: loginHref,
            isLoggedIn: user.isLoggedIn
          })]
        }) : /*#__PURE__*/_jsxs(ResponsiveContainer, {
          children: [/*#__PURE__*/_jsx(LeftSection, {
            children: /*#__PURE__*/_jsx(ReviewsLeftColumn, {
              hosting: hosting,
              isLoggedIn: user.isLoggedIn,
              loginHref: loginHref,
              currentSortOption: currentSortOption,
              handleShowReviewForm: onShowReviewForm,
              sortReviewsBy: sortReviewsBy,
              selfUserReview: reviewsData.selfUserReview,
              appRatingByHostingType: reviewsData.appRating,
              appRating: appRating,
              productHostingOptions: productHostingOptions,
              isServerAppEos: isServerAppEos
            })
          }), /*#__PURE__*/_jsxs(RightSection, {
            id: ELEMENT_ID.REVIEW_GUIDELINE_BANNER_AND_FORM,
            children: [ownReviewId && !noOwnReviewPosted ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(OwnReview, {
                initialReviewData: reviewsData.selfUserReviewData,
                ownReviewId: ownReviewId,
                mpacBaseUrl: mpacBaseUrl,
                onEditReview: onShowReviewForm,
                user: user,
                appKey: appKey,
                ownReviewUpdated: ownReviewUpdated,
                setOwnReviewUpdated: setOwnReviewUpdated,
                filterOutDeletedReview: handleFilterOutDeletedReview,
                filterOutDeletedVendorResponse: filterOutDeletedVendorResponse,
                loginHref: loginHref,
                vendorDetails: {
                  name: vendorName,
                  logo: vendorLogo
                },
                hosting: hosting
              }), (reviewsData.reviews.length !== 0 || showSortLoader) && /*#__PURE__*/_jsx(ReviewsHeader, {
                ownReviewPresent: true,
                totalReviews: reviewsData.appRating.numRatings,
                currentSortOption: currentSortOption,
                sortReviewsBy: sortReviewsBy
              })]
            }) : /*#__PURE__*/_jsx(ReviewsHeader, {
              totalReviews: reviewsData.appRating.numRatings,
              currentSortOption: currentSortOption,
              sortReviewsBy: sortReviewsBy
            }), /*#__PURE__*/_jsxs(UserReviewsWrapper, {
              children: [reviewsData.reviews.length > 0 && /*#__PURE__*/_jsx(UserReviewsV2, {
                reviews: reviewsData.reviews,
                mpacBaseUrl: mpacBaseUrl,
                user: user,
                appKey: appKey,
                filterOutDeletedReview: handleFilterOutDeletedReview,
                filterOutDeletedVendorResponse: filterOutDeletedVendorResponse,
                loginHref: loginHref,
                vendorName: vendorName,
                vendorLogo: vendorLogo
              }), showSortLoader && /*#__PURE__*/_jsx(ReviewsSkeleton, {
                sorting: true
              }), showMoreReviewsLoader && /*#__PURE__*/_jsx(ReviewsLoadMoreSkeleton, {})]
            }), reviewsData.hasMoreReviews ? /*#__PURE__*/_jsx(MoreReviewsButtonWrapper, {
              children: /*#__PURE__*/_jsx(Button, {
                appearance: "default",
                isLoading: showMoreReviewsLoader,
                onClick: handleSeeMoreReviews,
                "aria-label": "Load more reviews",
                children: "Read more reviews"
              })
            }) : null]
          })]
        })
      })]
    });
  }
  if (error) log.error('ReviewsTab', 'failed to fetch the reviews', {
    error
  });
  return fallbackUI();
};
export default ReviewsContainerV2;